import React from "react";
import { motion } from "framer-motion";
import { BrainCircuit, BookOpen, PenTool, Video, Laptop, Target, LayoutDashboard, Database, ArrowUpRight } from "lucide-react";

export default function SaasWhySkillzy() {
  const features = [
    {
      title: "Multi-Tenant SaaS Architecture",
      desc: "Each institute receives a fully independent, isolated space with custom branding, subdomains, database storage, and asset CDNs.",
      icon: <Database size={24} />,
      color: "from-violet-500 to-indigo-500",
      bg: "bg-violet-50/50 dark:bg-violet-950/10 border-violet-100 dark:border-violet-900/30"
    },
    {
      title: "24/7 AI Tutor Assistant",
      desc: "Personalized study feedback, instant doubt resolution, conceptual breakdowns, and smart suggestions for every student based on syllabus progress.",
      icon: <BrainCircuit size={24} />,
      color: "from-blue-500 to-cyan-500",
      bg: "bg-blue-50/50 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900/30"
    },
    {
      title: "AI Exam & Question Generator",
      desc: "Autogenerate dynamic exams, chapter quizzes, and comprehensive assessments matching specified syllabus topics and difficulty levels in seconds.",
      icon: <BookOpen size={24} />,
      color: "from-cyan-500 to-teal-500",
      bg: "bg-cyan-50/50 dark:bg-cyan-950/10 border-cyan-100 dark:border-cyan-900/30"
    },
    {
      title: "Subjective Digital Answer Sheets",
      desc: "Full support for digital stylus handwriting, canvas writing inputs, PDF script exports, and a hybrid AI + teacher assessment marking workflow.",
      icon: <PenTool size={24} />,
      color: "from-purple-500 to-pink-500",
      bg: "bg-purple-50/50 dark:bg-purple-950/10 border-purple-100 dark:border-purple-900/30"
    },
    {
      title: "HD Live Classes & Streaming",
      desc: "Integrate video rooms, schedule online meetings, track automated attendance logs, and generate lecture transcripts automatically.",
      icon: <Video size={24} />,
      color: "from-emerald-500 to-green-500",
      bg: "bg-emerald-50/50 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/30"
    },
    {
      title: "Interactive Coding Arena",
      desc: "Complete compiler sandbox environment supporting multiple programming languages, customized test-cases, and automated code marking.",
      icon: <Laptop size={24} />,
      color: "from-rose-500 to-orange-500",
      bg: "bg-rose-50/50 dark:bg-rose-950/10 border-rose-100 dark:border-rose-900/30"
    },
    {
      title: "Targeted Placement Preparation",
      desc: "Help college students stand out with resume-building algorithms, automated aptitude practice tests, and AI-led mock behavioral interviews.",
      icon: <Target size={24} />,
      color: "from-amber-500 to-yellow-500",
      bg: "bg-amber-50/50 dark:bg-amber-950/10 border-amber-100 dark:border-amber-900/30"
    },
    {
      title: "Detailed Parent Dashboard",
      desc: "Build trust with real-time analytics. Provide parents visual marks records, attendance charts, home assignments progress, and teacher feedback.",
      icon: <LayoutDashboard size={24} />,
      color: "from-teal-500 to-blue-500",
      bg: "bg-teal-50/50 dark:bg-teal-950/10 border-teal-100 dark:border-teal-900/30"
    }
  ];

  return (
    <section id="features" className="py-24 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Why Institutes Choose Skillzy
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Discover the state-of-the-art features that place Skillzy years ahead of traditional learning management systems.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -8 }}
              className={`p-6 rounded-[2rem] border bg-white dark:bg-slate-950/50 hover:shadow-xl dark:hover:shadow-violet-950/10 transition-all duration-300 group flex flex-col justify-between`}
            >
              <div>
                {/* Feature Icon with gradient background */}
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${feature.color} text-white flex items-center justify-center shadow-lg shadow-indigo-500/10 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                
                <h3 className="text-lg font-black text-slate-800 dark:text-white mt-6 mb-3 leading-snug">
                  {feature.title}
                </h3>
                
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-semibold">
                  {feature.desc}
                </p>
              </div>

              {/* Action indicator at bottom */}
              <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center text-xs font-bold text-slate-400 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                <span>Learn more</span>
                <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all" />
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
